import React, { useMemo } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const ROIDashboard = ({ roiData, loading }) => {
  const { currentLanguage, t } = useLanguage();

  const kpiMetrics = useMemo(() => {
    if (!roiData?.groups || roiData?.groups?.length === 0) {
      return {
        totalROI: 0,
        avgMargin: 0,
        totalRevenue: 0,
        totalCosts: 0,
        totalProfit: 0,
        profitableGroups: 0
      };
    }

    const totalRevenue = roiData?.groups?.reduce((sum, g) => sum + parseFloat(g?.revenue || 0), 0);
    const totalCosts = roiData?.groups?.reduce((sum, g) => sum + parseFloat(g?.costs || 0), 0);
    const totalProfit = totalRevenue - totalCosts;
    const totalROI = totalCosts > 0 ? ((totalProfit / totalCosts) * 100) : 0;
    const avgMargin = roiData?.groups?.length > 0 
      ? roiData?.groups?.reduce((sum, g) => sum + (parseFloat(g?.margin || 0)), 0) / roiData?.groups?.length 
      : 0;
    const profitableGroups = roiData?.groups?.filter(g => parseFloat(g?.profit || 0) > 0)?.length;

    return {
      totalROI,
      avgMargin,
      totalRevenue,
      totalCosts,
      totalProfit,
      profitableGroups
    };
  }, [roiData]);

  const seasonalTrends = useMemo(() => {
    if (!roiData?.groups || roiData?.groups?.length === 0) return [];

    const groupsByMonth = {};
    roiData?.groups?.forEach(group => {
      if (group?.departureDate) {
        const date = new Date(group?.departureDate);
        const monthKey = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
        
        if (!groupsByMonth?.[monthKey]) {
          groupsByMonth[monthKey] = {
            month: monthKey,
            revenue: 0,
            costs: 0,
            profit: 0,
            roi: 0,
            count: 0
          };
        }
        
        groupsByMonth[monthKey].revenue += parseFloat(group?.revenue || 0);
        groupsByMonth[monthKey].costs += parseFloat(group?.costs || 0);
        groupsByMonth[monthKey].profit += parseFloat(group?.profit || 0);
        groupsByMonth[monthKey].count += 1;
      }
    });

    return Object.values(groupsByMonth)
      ?.map(month => ({
        ...month,
        roi: month?.costs > 0 ? ((month?.profit / month?.costs) * 100) : 0,
        avgMargin: month?.revenue > 0 ? ((month?.profit / month?.revenue) * 100) : 0
      }))
      ?.sort((a, b) => a?.month?.localeCompare(b?.month));
  }, [roiData]);

  const formatCurrency = (amount) => {
    if (currentLanguage === 'ar') {
      const formattedNumber = new Intl.NumberFormat('ar-MA', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })?.format(amount);
      return `${formattedNumber} ${t('financial.currency')}`;
    } else {
      return new Intl.NumberFormat('fr-MA', {
        style: 'currency',
        currency: 'MAD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })?.format(amount);
    }
  };

  const formatMonth = (monthKey) => {
    const [year, month] = monthKey?.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date?.toLocaleDateString(currentLanguage === 'ar' ? 'ar-MA' : 'fr-FR', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-warm-md">
          <p className="text-sm font-medium text-foreground mb-2">
            {formatMonth(payload?.[0]?.payload?.month)}
          </p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm font-semibold data-text" style={{ color: entry?.color }}>
              {entry?.name}: {entry?.dataKey === 'roi' || entry?.dataKey === 'avgMargin' 
                ? `${entry?.value?.toFixed(1)}%` 
                : formatCurrency(entry?.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-warm p-6 animate-pulse">
        <div className="h-6 bg-muted rounded w-48 mb-6"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="h-24 bg-muted rounded"></div>
          <div className="h-24 bg-muted rounded"></div>
          <div className="h-24 bg-muted rounded"></div>
        </div>
        <div className="h-80 bg-muted rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">
          {currentLanguage === 'fr' ? 'Tableau de bord ROI' : 'لوحة تحليل العائد على الاستثمار'}
        </h3>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Icon name="TrendingUp" size={16} />
          <span>{currentLanguage === 'fr' ? 'Analyse de rentabilité' : 'تحليل الربحية'}</span>
        </div>
      </div>

      {/* KPI Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div className="bg-primary/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Percent" size={16} className="text-primary" />
            <p className="caption text-muted-foreground">
              {currentLanguage === 'fr' ? 'ROI Total' : 'إجمالي العائد'}
            </p>
          </div>
          <p className="text-2xl font-bold text-primary data-text">
            {kpiMetrics?.totalROI?.toFixed(1)}%
          </p>
        </div>

        <div className="bg-success/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <p className="caption text-muted-foreground">
              {currentLanguage === 'fr' ? 'Marge Moy.' : 'متوسط الهامش'}
            </p>
          </div>
          <p className="text-2xl font-bold text-success data-text">
            {kpiMetrics?.avgMargin?.toFixed(1)}%
          </p>
        </div>

        <div className="bg-blue-500/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="DollarSign" size={16} className="text-blue-500" />
            <p className="caption text-muted-foreground">
              {currentLanguage === 'fr' ? 'Revenus' : 'الإيرادات'}
            </p>
          </div>
          <p className="text-xl font-bold text-blue-500 data-text">
            {formatCurrency(kpiMetrics?.totalRevenue)}
          </p>
        </div>

        <div className="bg-orange-500/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="ShoppingCart" size={16} className="text-orange-500" />
            <p className="caption text-muted-foreground">
              {currentLanguage === 'fr' ? 'Coûts' : 'التكاليف'}
            </p>
          </div>
          <p className="text-xl font-bold text-orange-500 data-text">
            {formatCurrency(kpiMetrics?.totalCosts)}
          </p>
        </div>

        <div className="bg-green-500/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="PiggyBank" size={16} className="text-green-500" />
            <p className="caption text-muted-foreground">
              {currentLanguage === 'fr' ? 'Profit' : 'الربح'}
            </p>
          </div>
          <p className="text-xl font-bold text-green-500 data-text">
            {formatCurrency(kpiMetrics?.totalProfit)}
          </p>
        </div>

        <div className="bg-purple-500/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="CheckCircle" size={16} className="text-purple-500" />
            <p className="caption text-muted-foreground">
              {currentLanguage === 'fr' ? 'Rentables' : 'مربحة'}
            </p>
          </div>
          <p className="text-2xl font-bold text-purple-500 data-text">
            {kpiMetrics?.profitableGroups}/{roiData?.groups?.length || 0}
          </p>
        </div>
      </div>

      {/* Seasonal Trends Chart */}
      <div>
        <h4 className="text-sm font-medium text-muted-foreground mb-4">
          {currentLanguage === 'fr' ? 'Tendances Saisonnières' : 'الاتجاهات الموسمية'}
        </h4>
        {seasonalTrends?.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={seasonalTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                tickFormatter={formatMonth}
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                yAxisId="left"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `${(value / 1000)?.toFixed(0)}k`}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `${value?.toFixed(0)}%`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="revenue" 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={{ fill: '#3b82f6', r: 4 }}
                name={currentLanguage === 'fr' ? 'Revenus' : 'الإيرادات'}
              />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="costs" 
                stroke="#f97316" 
                strokeWidth={2}
                dot={{ fill: '#f97316', r: 4 }}
                name={currentLanguage === 'fr' ? 'Coûts' : 'التكاليف'}
              />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="profit" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={{ fill: '#10b981', r: 4 }}
                name={currentLanguage === 'fr' ? 'Profit' : 'الربح'}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="roi" 
                stroke="#8b5cf6" 
                strokeWidth={3}
                dot={{ fill: '#8b5cf6', r: 5 }}
                name={currentLanguage === 'fr' ? 'ROI (%)' : 'العائد (%)'}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-64 bg-muted/50 rounded-lg">
            <p className="text-muted-foreground">
              {currentLanguage === 'fr' ? 'Aucune donnée disponible' : 'لا توجد بيانات متاحة'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ROIDashboard;